/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';

import { usePhotoDropZone } from '@/Client/Widgets/PhotoDropZone';

import { useProgressAlt } from './progressAltContext';
import type { ProgressAltColumn } from './progressAltModel';

/**
 * Laid over the column at exactly the column's own place and width, which the grid already knows
 * and hands in as pixels. Out of the grid's flow entirely, so nothing else moves while it shows.
 * Stacked over the sticky header row, and under the sticky date column.
 */
const ZONE_STYLE = css`
  label: PROGRESS_ALT_COLUMN_DROP_ZONE_STYLE;
  position: absolute;
  top: 0;
  bottom: 0;
  z-index: 1;
  box-sizing: border-box;
`;

/** An outline and nothing more, so the tiles it is about to drop onto stay in plain view. */
const ZONE_LIT_STYLE = css`
  border: 2px solid var(--textBlue);
`;

/**
 * One column's drop target: photos dropped anywhere on it are uploaded into the group or task it
 * stands for. Drawn only while images are being dragged, so it never stands between a reader and
 * the tiles underneath it; outlined only while the drag is over this column.
 */
export const ProgressAltColumnDropZone = ({ column, leftPx, widthPx }: {
  column: ProgressAltColumn;
  /** The column's left edge, measured from the grid's. */
  leftPx: number;
  /** The column's width as laid out: one tile wide, or every tile wide when expanded. */
  widthPx: number;
}) => {
  const { scanRoot } = useProgressAlt();
  const group = scanRoot?.groupsById[column.groupId];
  const { isDragOver, handlers } = usePhotoDropZone(
    column.taskId ? group?.tasksById[column.taskId] : group,
    { groupId: column.groupId, taskId: column.taskId },
  );
  return (
    <div
      css={[ZONE_STYLE, isDragOver && ZONE_LIT_STYLE]}
      style={{ left: leftPx, width: widthPx }}
      data-testid='progress-alt-column-drop-zone'
      data-progress-alt-drop-lit={isDragOver ? 'true' : undefined}
      aria-label={`Drop photos into ${column.title}`}
      {...handlers}
    />
  );
};
